<template>
  <div class="w-100">
    <div class="px-8 pt-8 pb-4">
      <h2 class="text-h2 text-textDarkest mb-4">
        Job Profiles using this Template
      </h2>

      <p class="text-body-1 text-textDark mb-0">
        {{ associatedProfilesCopy }}
      </p>
      <p
        class="text-body-1 text-textDark mt-4 mb-0"
        role="status"
      >
        {{ outdatedProfilesCopy }}
      </p>

      <div
        v-if="outdatedCount > 0"
        class="d-flex ga-3 mt-4"
      >
        <DpplNewButton
          color="primary"
          variant="outlined"
          height="40"
          class="flex-grow-1"
          @click="emit('select-outdated')"
        >
          {{ selectOutdatedLabel }}
        </DpplNewButton>
        <DpplNewButton
          color="primary"
          height="40"
          class="template-usage-update-profiles-btn flex-shrink-0"
          :disabled="selectedUlids.length === 0"
          @click="emit('open-confirm')"
        >
          Update profiles
        </DpplNewButton>
      </div>
    </div>

    <div
      v-if="rows.length"
      class="template-usage-list"
    >
      <TemplateUsageListItem
        v-for="row in rows"
        :key="row.profile.ulid"
        :profile="row.profile"
        :status="row.status"
        :selected="selectedUlids.includes(row.profile.ulid)"
        @toggle="emit('toggle', $event)"
      />
    </div>

    <p
      v-else
      class="text-body-1 text-textDark px-8 py-3"
    >
      No profiles have been built from this template yet.
    </p>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import TemplateUsageListItem from '@/components/jobProfiles/editor/TemplateUsageListItem.vue';

const props = defineProps({
  rows: {
    type: Array,
    default: () => [],
  },
  selectedUlids: {
    type: Array,
    default: () => [],
  },
  outdatedCount: {
    type: Number,
    default: 0,
  },
  profileCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['toggle', 'select-outdated', 'open-confirm']);

const associatedProfilesCopy = computed(() => (
  props.profileCount === 1
    ? 'This template is associated with 1 profile.'
    : `This template is associated with ${props.profileCount} profiles.`
));

const outdatedProfilesCopy = computed(() => {
  if (props.outdatedCount <= 0) {
    return 'No profiles have outdated template content.';
  }

  return props.outdatedCount === 1
    ? '1 of these profiles has outdated template content.'
    : `${props.outdatedCount} of these profiles have outdated template content.`;
});

const outdatedUlids = computed(() => (
  props.rows
    .filter(({ status }) => status === 'outdated')
    .map(({ profile }) => profile.ulid)
));

const allOutdatedSelected = computed(() => (
  outdatedUlids.value.length > 0
  && outdatedUlids.value.every((ulid) => props.selectedUlids.includes(ulid))
));

const selectOutdatedLabel = computed(() => {
  const action = allOutdatedSelected.value ? 'Unselect' : 'Select';

  return props.outdatedCount === 1
    ? `${action} 1 outdated profile`
    : `${action} ${props.outdatedCount} outdated profiles`;
});
</script>

<style scoped lang="less">
.template-usage-update-profiles-btn {
  width: 125px;
}
</style>
